import { IS_CLIENT, useIsomorphicLayoutEffect } from '@repo/common-utils';
import { useContext } from 'react';

import { EventTrackingContext } from './components/EventTrackingProvider';
import {
  DatadogEventData,
  KakaoPixelEventParams,
  KakaoPixelMethods,
  MetaPixelEventNames,
  MetaPixelParams,
  NaverPixelEventParams,
  SendEventOptions,
  SendEventParams
} from './types';

export const useUserEventLogger = (currentPageName?: string) => {
  const {
    sendEvent,
    sendMetaPixelEvent,
    sendKakaoPixelEvent,
    sendNaverPixelEvent,
    addSentEventHistory,
    eventUser,
    pageName,
    setPageName,
    sendDatadogEvent,
    setUser,
    unsetUser,
    properties: { mixpanelDeviceId }
  } = useContext(EventTrackingContext);
  const convertEventData = (eventName: string, action: string = '', eventProperty?: Record<string, unknown>) => {
    const [category, object, ...rest] = eventName.split('_');

    return {
      category,
      object,
      detail: rest?.join('_') || undefined,
      action,
      attributes: Object.assign({ page: pageName, user_email: eventUser?.email, user_access_type: eventUser?.accessType }, eventProperty ?? {})
    } as SendEventParams;
  };

  const sendViewEvent = (eventName: string, eventProperty?: Record<string, unknown>, options?: SendEventOptions) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (!IS_CLIENT || !eventName || typeof eventUser === 'undefined' || (searchParams.has('code') && searchParams.has('grant_type'))) return;
    const eventData = convertEventData(eventName, 'viewed', eventProperty);

    if (pageName && addSentEventHistory(eventData, options?.isAllowRetry)) {
      sendEvent(eventData, options);
    }

    if (addSentEventHistory({ category: 'pixel', object: pageName })) {
      sendMetaPixelEvent('PageView');
      sendKakaoPixelEvent('pageView', pageName);
    }
  };

  const sendClickEvent = (eventName: string, eventProperty?: Record<string, unknown>, options?: SendEventOptions) => {
    const eventData = convertEventData(eventName, 'clicked', eventProperty);

    sendEvent(eventData, options);
  };

  const sendScrollEvent = (eventName: string, eventProperty?: Record<string, unknown>, options?: SendEventOptions) => {
    const eventData = convertEventData(eventName, 'scrolled', eventProperty);

    sendEvent(eventData, options);
  };

  const sendRequestEvent = (eventName: string, eventProperty?: Record<string, unknown>, options?: SendEventOptions) => {
    const eventData = convertEventData(eventName, 'request', eventProperty);

    sendEvent(eventData, options);
  };

  const sendCustomActionEvent = (eventName: string, action: string, eventProperty?: Record<string, unknown>, options?: SendEventOptions) => {
    const eventData = convertEventData(eventName, action, eventProperty);

    sendEvent(eventData, options);
  };

  const sendMetaEvent = (eventName: MetaPixelEventNames, eventProperty?: MetaPixelParams) => {
    sendMetaPixelEvent(eventName, eventProperty);
  };

  const sendKakaoEvent = (methodName: KakaoPixelMethods, eventProperty?: KakaoPixelEventParams) => {
    sendKakaoPixelEvent(methodName, eventProperty);
  };

  const sendNaverEvent = (params: NaverPixelEventParams) => {
    sendNaverPixelEvent(params);
  };

  const sendDatadogLog = (data: DatadogEventData) => {
    sendDatadogEvent(data);
  };

  useIsomorphicLayoutEffect(() => {
    if (currentPageName && currentPageName !== pageName) setPageName(currentPageName);
  }, [currentPageName, pageName, setPageName]);

  return {
    sendViewEvent,
    sendClickEvent,
    sendScrollEvent,
    sendRequestEvent,
    sendCustomActionEvent,
    sendMetaEvent,
    sendKakaoEvent,
    sendNaverEvent,
    sendDatadogLog,
    pageName,
    setUser,
    unsetUser,
    mixpanelDeviceId
  };
};
